import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { getSupabaseClient } from '../services/supabase';
import { usePostHog } from 'posthog-js/react';
import { getCookie } from '../services/analytics';
import { isDuplicateEvent } from '../utils/eventDeduplication';
import { useDeduplicatedPostHog } from '../utils/posthogWrapper';
import { deduplicatedPostHogCall } from '../utils/posthogDeduplication';

export type OAuthProvider = 'google' | 'apple' | 'discord' | 'twitter' | 'github';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAuthenticated: boolean;
  signInWithOAuth: (provider: OAuthProvider) => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | undefined>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Component to handle PostHog identify when user changes
function PostHogIdentify({ user }: { user: User | null }) {
  const posthog = useDeduplicatedPostHog(); // Use deduplicated wrapper
  const identifiedUserIdRef = useRef<string | null>(null);
  const lastPropertiesHashRef = useRef<string | null>(null);

  useEffect(() => {
    if (!posthog) return;

    // If user is logged in and we haven't identified them yet, or user changed
    if (user && user.email && user.id !== identifiedUserIdRef.current) {
      const userProperties: Record<string, any> = {
        email: user.email,
        user_id: user.id,
        external_id: user.id,
      };

      // Add additional user metadata if available
      if (user.user_metadata?.full_name) {
        userProperties.name = user.user_metadata.full_name;
      }
      if (user.app_metadata?.provider) {
        userProperties.auth_provider = user.app_metadata.provider;
      }
      if (user.created_at) {
        userProperties.created_at = user.created_at;
      }

      // Create hash of properties to check if they've changed
      const propertiesHash = JSON.stringify(userProperties);

      // Only identify if properties actually changed
      if (propertiesHash !== lastPropertiesHashRef.current) {
        posthog.identify(user.id, userProperties);
        identifiedUserIdRef.current = user.id;
        lastPropertiesHashRef.current = propertiesHash;
      }
    } else if (!user && identifiedUserIdRef.current) {
      // User logged out - reset identification
      posthog.reset();
      identifiedUserIdRef.current = null;
      lastPropertiesHashRef.current = null;
    }
  }, [user, posthog]);

  return null;
}

// Helper function to generate event ID (same as in useTracking)
function generateEventId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Helper function to track events (similar to useTracking but without useAuth dependency)
function trackAuthEvent(
  posthog: any,
  eventName: string,
  properties: Record<string, any>,
  user: User | null
) {
  // Build event data with user information FIRST (before deduplication check)
  const eventData: Record<string, any> = {
    ...properties,
    // Always include user identifiers if available
    external_id: user?.id || properties.external_id || '',
    user_id: user?.id || properties.user_id || '',
  };

  // Add email if user is authenticated OR if email is in properties (for signup events)
  // Priority: properties.email (from session.user.email) > user.email
  if (properties.email) {
    eventData.email = properties.email;
  } else if (user?.email) {
    eventData.email = user.email;
  }

  // Store email before cleanup (for GTM protection)
  const emailValue = eventData.email;

  // Remove empty values to keep payload clean (but preserve email for signup/login events)
  Object.keys(eventData).forEach(key => {
    // Don't remove email if it exists (even if empty string, for signup/login it should be present)
    if (key === 'email' && eventData.email) {
      return; // Keep email
    }
    if (eventData[key] === undefined || eventData[key] === null || eventData[key] === '') {
      delete eventData[key];
    }
  });

  // Check for duplicate events AFTER merging user data (so hash is accurate)
  if (isDuplicateEvent(eventName, eventData)) {
    return; // Event already tracked recently, skip
  }

  // Generate unique event ID if not provided
  if (!eventData.event_id) {
    eventData.event_id = generateEventId();
  }

  // Get Facebook cookies (for both GTM and PostHog)
  const fbp = typeof window !== 'undefined' ? (getCookie('_fbp') || '') : '';
  const fbc = typeof window !== 'undefined' ? (getCookie('_fbc') || '') : '';

  // Add Facebook cookies to eventData (for PostHog)
  // fbp should always be included (even if empty), fbc only if present
  eventData.fbp = fbp;
  if (fbc) {
    eventData.fbc = fbc;
  }

  // Track if PostHog event was sent (to prevent retry duplicates)
  const posthogEventSent = { value: false };

  // Send to Google Tag Manager (GTM) - always send, even if PostHog isn't ready
  if (typeof window !== 'undefined') {
    // Initialize dataLayer if needed
    window.dataLayer = window.dataLayer || [];

    // Get user ID from event data
    const userId = eventData.user_id || eventData.external_id || '';
    const externalId = eventData.external_id || userId || '';

    // Build GTM payload
    const gtmPayload: Record<string, any> = {
      event: eventName,
      event_id: eventData.event_id,
      external_id: externalId,
      fbp: fbp,
      ...eventData, // Include all event-specific data (including fbp and fbc)
    };

    // Add user_id if available
    if (userId) {
      gtmPayload.user_id = userId;
    }

    // Remove empty values (except fbp and email which should always be included if present)
    // Email is critical for signup/login events and should not be removed
    Object.keys(gtmPayload).forEach(key => {
      // Protect fbp, event, and email from deletion
      if (key === 'fbp' || key === 'event' || key === 'email') {
        return; // Keep these fields
      }
      if (gtmPayload[key] === undefined || gtmPayload[key] === null || gtmPayload[key] === '') {
        delete gtmPayload[key];
      }
    });
    
    // Ensure email is included if it was in the original eventData (for signup/login events)
    if (emailValue && !gtmPayload.email) {
      gtmPayload.email = emailValue;
    }

    // Push to dataLayer (GTM)
    window.dataLayer.push(gtmPayload);
  }

  // Send to PostHog
  // Note: posthog parameter here is from usePostHog(), but we need to wrap it
  // Since this is a helper function, we'll use deduplicatedPostHogCall directly
  const sendToPostHog = () => {
    // Check if already sent (prevent retry duplicates)
    if (posthogEventSent.value) {
      return true;
    }

    if (posthog && typeof posthog.capture === 'function') {
      // Use deduplicated call - this will prevent duplicates even if called multiple times
      // eventData now includes fbp and fbc
      deduplicatedPostHogCall(posthog, 'capture', eventName, eventData);
      posthogEventSent.value = true; // Mark as sent
      return true;
    }
    return false;
  };

  if (!sendToPostHog()) {
    console.warn('⚠️ [PostHog] PostHog not initialized, skipping event:', eventName);
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const posthog = usePostHog();
  const previousUserIdRef = useRef<string | null>(null);
  const trackedSignupsRef = useRef<Set<string>>(new Set());
  // Track seen user IDs to determine if user is new or existing
  // This prevents incorrectly marking existing users as new on initial page load
  const seenUserIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let mounted = true;
    let subscription: { unsubscribe: () => void } | null = null;

    const initializeAuth = async () => {
      try {
        const supabase = getSupabaseClient();

        // Clean up stale OAuth flags (older than 5 minutes)
        const oauthInitiatedTime = sessionStorage.getItem('genanything_oauth_initiated');
        if (oauthInitiatedTime) {
          const timeSinceOAuth = Date.now() - parseInt(oauthInitiatedTime);
          if (timeSinceOAuth > 300000) { // 5 minutes
            sessionStorage.removeItem('genanything_oauth_initiated');
            sessionStorage.removeItem('genanything_oauth_provider');
          }
        }

        // Get initial session from Supabase
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting session:', error);
        }

        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
          const initialUserId = session?.user?.id ?? null;
          previousUserIdRef.current = initialUserId;

          // If we have an initial session, mark this user as seen (they're logging in, not signing up)
          // BUT: Don't mark as seen if this is an OAuth callback (let onAuthStateChange handle it)
          const urlParams = new URLSearchParams(window.location.search);
          const oauthFlowFromUrl = urlParams.get('oauth_flow') === 'true';
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const hasAccessTokenInHash = hashParams.has('access_token');
          const isOAuthProvider = session?.user?.app_metadata?.provider &&
            ['google', 'apple', 'discord', 'twitter', 'github'].includes(session.user.app_metadata.provider);

          const isOAuthCallback = !!(
            (oauthInitiatedTime && sessionStorage.getItem('genanything_oauth_provider')) ||
            oauthFlowFromUrl ||
            (hasAccessTokenInHash && isOAuthProvider) || // NEW: Detect OAuth from hash on ANY page
            (window.location.pathname === '/auth/callback' && isOAuthProvider)
          );

          if (initialUserId && session?.user?.email && !session.user.is_anonymous && !isOAuthCallback) {
            seenUserIdsRef.current.add(initialUserId);
          }

          setLoading(false);
        }

        // Listen for auth changes
        const {
          data: { subscription: authSubscription },
        } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
          if (mounted) {
            const previousUserId = previousUserIdRef.current;
            const currentUserId = session?.user?.id ?? null;

            setSession(session);
            setUser(session?.user ?? null);
            setLoading(false);

            // Check if this is an OAuth callback completion (multi-layered detection)
            // Method 1: SessionStorage (primary)
            const oauthInitiatedTime = sessionStorage.getItem('genanything_oauth_initiated');
            const oauthProviderFromStorage = sessionStorage.getItem('genanything_oauth_provider');

            // Method 2: URL parameters (backup - check if we're on auth callback page or have hash token)
            const urlParams = new URLSearchParams(window.location.search);
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            const hasAccessTokenInHash = hashParams.has('access_token');
            const isOnAuthCallbackPage = typeof window !== 'undefined' &&
              (window.location.pathname === '/auth/callback' || hasAccessTokenInHash);
            const oauthFlowFromUrl = urlParams.get('oauth_flow') === 'true';
            const oauthProviderFromUrl = urlParams.get('oauth_provider');

            // Method 3: Supabase metadata (fallback - check if provider is OAuth)
            const providerFromMetadata = session?.user?.app_metadata?.provider;
            const isOAuthProvider = providerFromMetadata && ['google', 'apple', 'discord', 'twitter', 'github'].includes(providerFromMetadata);

            // Add timestamp check to prevent stale OAuth flags from re-triggering
            const withinTimeWindow = oauthInitiatedTime && (Date.now() - parseInt(oauthInitiatedTime)) < 30000; // 30 seconds (was 120000)

            // Determine if this is an OAuth callback
            // ONLY consider sessionStorage if within time window (prevent stale flags)
            const isOAuthCallback = !!(
              (withinTimeWindow && oauthProviderFromStorage) || // SessionStorage detected AND recent
              (oauthFlowFromUrl && oauthProviderFromUrl) || // URL parameter detected
              (isOnAuthCallbackPage && isOAuthProvider && withinTimeWindow) // On callback page with OAuth provider AND recent
            );

            // Use the most reliable provider source
            const oauthProvider = oauthProviderFromStorage || oauthProviderFromUrl || providerFromMetadata;

            // Track signup/login events
            if (session?.user && session.user.email && !session.user.is_anonymous) {

              // Check if this is a new sign-in
              // For OAuth: accept both SIGNED_IN and INITIAL_SESSION events if OAuth was just initiated
              // For email OTP: only accept SIGNED_IN events
              const isSignedInEvent = event === 'SIGNED_IN';
              const isInitialSessionEvent = event === 'INITIAL_SESSION';

              // OAuth is returning if:
              // 1. We detected it's an OAuth callback (via ANY of the 3 methods), AND
              // 2. It's an INITIAL_SESSION event, AND  
              // 3. We're on the auth callback page
              // The combination of these three things makes it almost certain this is an OAuth return
              const confirmedByUrlParams = oauthFlowFromUrl && oauthProviderFromUrl;
              const confirmedByMetadata = isOnAuthCallbackPage && isOAuthProvider;
              const isOAuthReturning = isOAuthCallback && isInitialSessionEvent &&
                (withinTimeWindow || confirmedByUrlParams || confirmedByMetadata);

              // CRITICAL: Clean up OAuth flags to prevent stale redirects on other pages
              // BUT: Don't clean on /auth/callback - let AuthCallbackPage validate first
              // This prevents race condition where flags are cleared before AuthCallbackPage reads them
              // Check pathname only (not hash) - use strict pathname check for cleanup logic
              const isOnCallbackPageForCleanup = typeof window !== 'undefined' && window.location.pathname === '/auth/callback';

              if ((isOAuthCallback || isOAuthReturning) && !isOnCallbackPageForCleanup) {
                console.log('🧹 [Auth] Cleaning up OAuth flags (not on callback page)');
                try {
                  sessionStorage.removeItem('genanything_oauth_initiated');
                  sessionStorage.removeItem('genanything_oauth_provider');
                  sessionStorage.removeItem('oauth_initiated_time');
                  sessionStorage.removeItem('oauth_provider');
                } catch (e) {
                  console.error('[Auth] Failed to clear OAuth flags:', e);
                }
              }

              // For OAuth callbacks, we can't rely on previousUserId comparison since the session
              // is restored during initialization. Instead, we track if the user was seen before OAuth.
              const userWasSeenBeforeOAuth = currentUserId ? seenUserIdsRef.current.has(currentUserId) : false;

              // Determine if this is a new sign-in:
              // - For OAuth: user wasn't seen before OAuth was initiated
              // - For email OTP: user ID changed from previous
              const isNewSignIn = isOAuthReturning
                ? !userWasSeenBeforeOAuth  // OAuth: check if user was seen before OAuth flow
                : (isSignedInEvent && previousUserId !== currentUserId); // Email OTP: check user ID change

              const isUserJustCreated = session.user.created_at &&
                (new Date().getTime() - new Date(session.user.created_at).getTime()) < 60000; // Created within last minute

              // Check if we've seen this user ID before in this session
              const hasSeenThisUser = currentUserId ? seenUserIdsRef.current.has(currentUserId) : false;

              // Determine if it's a signup (new user) or login (existing user)
              // User is new ONLY if they were just created (within last minute)
              // If created more than 1 minute ago, they're an existing user (login)
              const isNewUser = isUserJustCreated;

              // Mark this user as seen when they sign in (for session tracking)
              if (isNewSignIn && currentUserId) {
                seenUserIdsRef.current.add(currentUserId);
              }

              // Only track if we haven't already tracked this user's signup/login
              // Use a more specific key that includes timestamp to prevent duplicates from rapid re-renders
              const eventKey = `${currentUserId}-${isNewUser ? 'signup' : 'login'}`;

              if (isNewSignIn && !trackedSignupsRef.current.has(eventKey)) {
                // Add to tracked set immediately to prevent duplicates
                trackedSignupsRef.current.add(eventKey);

                // Use OAuth provider if available, otherwise fall back to metadata
                const provider = oauthProvider || session.user.app_metadata?.provider || 'email';

                // Clear OAuth flags after tracking (all methods)
                // BUT: Don't clean on /auth/callback - let AuthCallbackPage handle it
                const isOnCallbackPageForTracking = typeof window !== 'undefined' && window.location.pathname === '/auth/callback';
                if (isOAuthCallback && !isOnCallbackPageForTracking) {
                  // Clear sessionStorage
                  sessionStorage.removeItem('genanything_oauth_initiated');
                  sessionStorage.removeItem('genanything_oauth_provider');
                }

                if (isNewUser) {

                  trackAuthEvent(
                    posthog,
                    'signup',
                    {
                      email: session.user.email,
                      method: provider,
                      external_id: currentUserId,
                      user_id: currentUserId,
                      is_new_user: true,
                    },
                    session.user
                  );
                } else {

                  trackAuthEvent(
                    posthog,
                    'login',
                    {
                      email: session.user.email,
                      method: provider,
                      external_id: currentUserId,
                      user_id: currentUserId,
                      is_new_user: false,
                    },
                    session.user
                  );
                }
              }
            }

            previousUserIdRef.current = currentUserId;
          }
        });

        subscription = authSubscription;
      } catch (error) {
        console.error('Failed to initialize Supabase auth:', error);
        if (mounted) {
          setUser(null);
          setSession(null);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [posthog]);

  // Add aggressive hash cleanup on mount and visibility change
  useEffect(() => {
    const cleanupOAuthHash = () => {
      if (typeof window !== 'undefined') {
        const hash = window.location.hash;
        if (hash.includes('access_token=') || hash.includes('error=')) {
          const hashParams = new URLSearchParams(hash.substring(1));
          const error = hashParams.get('error');
          const errorCode = hashParams.get('error_code');
          const oauthInitiatedTime = sessionStorage.getItem('genanything_oauth_initiated');
          const isRecentOAuth = oauthInitiatedTime && (Date.now() - parseInt(oauthInitiatedTime)) < 30000;
          const userAuthenticated = !!user;

          // Clean up if: error present OR user authenticated OR OAuth is stale
          // BUT: Don't clean on /auth/callback - let AuthCallbackPage handle it
          const isOnCallbackPage = window.location.pathname === '/auth/callback';
          const shouldClean = !isOnCallbackPage && (
            error ||
            (userAuthenticated && hash.includes('access_token=')) ||
            (!isRecentOAuth && hash.includes('access_token='))
          );

          if (shouldClean) {
            console.warn('[AuthContext] Cleaning up OAuth hash (not on callback page)', {
              reason: error ? 'error_present' : userAuthenticated ? 'user_authenticated' : 'oauth_stale',
              error,
              errorCode,
            });

            const cleanUrl = window.location.origin + window.location.pathname + window.location.search;
            window.history.replaceState(null, '', cleanUrl);

            // Also clean flags if stale
            if (!isRecentOAuth) {
              sessionStorage.removeItem('genanything_oauth_initiated');
              sessionStorage.removeItem('genanything_oauth_provider');
              sessionStorage.removeItem('oauth_initiated_time');
            }
          }
        }
      }
    };

    // Clean on mount
    cleanupOAuthHash();

    // Clean on visibility change (mobile browsers)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        cleanupOAuthHash();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]); // Depend on user instead of isAuthenticated

  const signInWithOAuth = async (provider: OAuthProvider) => {
    try {
      const supabase = getSupabaseClient();

      // Multi-layered approach for foolproof OAuth detection:
      // 1. SessionStorage (primary - works in most cases)
      const timestamp = Date.now().toString();
      sessionStorage.setItem('genanything_oauth_initiated', timestamp);
      sessionStorage.setItem('genanything_oauth_provider', provider);

      // 2. URL parameter (backup - always works, even if sessionStorage fails)
      // Add a marker to the callback URL so we can detect OAuth on return
      const currentPath = window.location.pathname;

      // CRITICAL: Store return path in BOTH sessionStorage and localStorage
      // Mobile browsers can clear sessionStorage under memory pressure
      if (currentPath !== '/' && currentPath !== '/auth/callback') {
        // Store in sessionStorage (primary)
        sessionStorage.setItem('oauth_return_path', currentPath);

        // localStorage backup in case sessionStorage gets cleared
        // Store with timestamp for validation
        localStorage.setItem('oauth_return_path_backup', currentPath);
        localStorage.setItem('oauth_return_path_timestamp', timestamp);

        // Additional backup: store full URL for better debugging
        localStorage.setItem('oauth_return_url_backup', window.location.href);

        console.log('✅ [OAuth] Stored return path:', {
          path: currentPath,
          sessionStorage: 'oauth_return_path',
          localStorage: 'oauth_return_path_backup',
          timestamp: timestamp
        });
      }

      const baseCallbackUrl = `${window.location.origin}/auth/callback`;
      const redirectParam = currentPath !== '/' ? `&redirect_to=${encodeURIComponent(currentPath)}` : '';
      const callbackUrl = `${baseCallbackUrl}?oauth_flow=true&oauth_provider=${provider}${redirectParam}`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: callbackUrl,
        },
      });
      if (error) throw error;
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const getAccessToken = async (): Promise<string | undefined> => {
    try {
      const supabase = getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token;
    } catch (error) {
      console.error('Error getting access token:', error);
      return undefined;
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    isAuthenticated: !!user,
    signInWithOAuth,
    signOut,
    getAccessToken,
  };

  return (
    <AuthContext.Provider value={value}>
      <PostHogIdentify user={user} />
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

